// RF / chat layer.
//
// The Cybiko's 902-928 MHz radio is emulated as a BroadcastChannel — every
// browser tab on the same origin is a virtual Cybiko within radio range.
// Worker → main 'rf-tx' messages are broadcast; incoming bytes from the
// channel are forwarded to the worker as 'rf-rx' with a small jitter that
// approximates real-RF arrival timing.
//
// Phase I: same-browser tabs only (BroadcastChannel scope).
// Phase II (deferred): WebRTC DataChannel via a Cloudflare Worker signaling
//   server for cross-user chat.

import type { MainToWorker, WorkerToMain } from './protocol';
import type { RfCaptureSession } from './rf-capture';

const CHANNEL_NAME = 'cybiko-rf';
const JITTER_MIN_MS = 6;
const JITTER_MAX_MS = 14;
const PRESERVE_SENDER_GAP_MS = 25;

/** Unique peer id for this tab so the worker can ignore its own broadcasts. */
function peerId(): string {
  return Math.random().toString(36).slice(2, 10);
}

interface RfMessage {
  readonly byte: number;
  readonly peer: string;
  readonly seq: number;
  readonly ts: number;
}

export interface RfBridgeOptions {
  readonly capture?: RfCaptureSession;
}

let channel: BroadcastChannel | null = null;
let myPeer = '';
let txSeq = 0;
interface PeerDeliveryQueue {
  readonly messages: RfMessage[];
  timer: ReturnType<typeof setTimeout> | undefined;
  nextDeliveryAtMs: number;
  lastSenderAtMs: number | undefined;
}

const peerDeliveryQueues = new Map<string, PeerDeliveryQueue>();

/**
 * Wire the worker's RF protocol to a BroadcastChannel. Returns a teardown
 * function that closes the channel and detaches the listener.
 */
export function initRf(worker: Worker, options: RfBridgeOptions = {}): () => void {
  if (typeof BroadcastChannel === 'undefined') {
    console.warn('BroadcastChannel unavailable — RF chat disabled');
    return () => {};
  }

  channel = new BroadcastChannel(CHANNEL_NAME);
  myPeer = peerId();
  const receiveEnabled = isRfReceiveEnabled();

  // Worker → channel
  const onWorkerMessage = (e: MessageEvent<WorkerToMain>): void => {
    if (e.data.type === 'rf-tx') {
      const msg: RfMessage = {
        byte: e.data.byte,
        peer: myPeer,
        seq: txSeq++,
        ts: Date.now(),
      };
      channel?.postMessage(msg);
    }
  };
  worker.addEventListener('message', onWorkerMessage);

  // Channel -> worker. Use a per-peer FIFO so long RF-facing byte streams
  // stay ordered while still preserving large sender-side timing gaps.
  channel.onmessage = (e: MessageEvent<RfMessage>): void => {
    if (e.data.peer === myPeer) return; // ignore our own echo
    if (!receiveEnabled) return;
    enqueuePeerDelivery(e.data, worker, options.capture);
  };

  return (): void => {
    worker.removeEventListener('message', onWorkerMessage);
    channel?.close();
    channel = null;
    txSeq = 0;
    clearPeerDeliveryQueues();
  };
}

function enqueuePeerDelivery(
  message: RfMessage,
  worker: Worker,
  capture: RfCaptureSession | undefined,
): void {
  let queue = peerDeliveryQueues.get(message.peer);
  if (!queue) {
    queue = {
      messages: [],
      timer: undefined,
      nextDeliveryAtMs: performance.now(),
      lastSenderAtMs: undefined,
    };
    peerDeliveryQueues.set(message.peer, queue);
  }
  queue.messages.push(message);
  if (queue.timer === undefined) {
    schedulePeerDelivery(message.peer, queue, worker, capture);
  }
}

function schedulePeerDelivery(
  peer: string,
  queue: PeerDeliveryQueue,
  worker: Worker,
  capture: RfCaptureSession | undefined,
): void {
  if (queue.messages.length === 0) {
    peerDeliveryQueues.delete(peer);
    return;
  }
  const next = queue.messages[0];
  const now = performance.now();
  const senderGap = queue.lastSenderAtMs === undefined
    ? 0
    : Math.max(0, next.ts - queue.lastSenderAtMs);
  const jitter = JITTER_MIN_MS + Math.random() * (JITTER_MAX_MS - JITTER_MIN_MS);
  const delay = queue.lastSenderAtMs === undefined
    ? jitter
    : senderGap > PRESERVE_SENDER_GAP_MS
      ? senderGap + jitter
      : 1;
  const deliveryAt = Math.max(now + delay, queue.nextDeliveryAtMs + 1);
  queue.nextDeliveryAtMs = deliveryAt;
  queue.timer = setTimeout(() => {
    queue.timer = undefined;
    const delivered = queue.messages.shift();
    if (delivered) {
      queue.lastSenderAtMs = delivered.ts;
      const rx: MainToWorker = { type: 'rf-rx', byte: delivered.byte, peerId: delivered.peer };
      capture?.recordMainMessage(rx, 'broadcast');
      worker.postMessage(rx);
    }
    schedulePeerDelivery(peer, queue, worker, capture);
  }, deliveryAt - now);
}

function clearPeerDeliveryQueues(): void {
  for (const queue of peerDeliveryQueues.values()) {
    if (queue.timer !== undefined) clearTimeout(queue.timer);
  }
  peerDeliveryQueues.clear();
}

function isRfReceiveEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  return new URLSearchParams(window.location.search).get('rfRx') !== '0';
}
